import { Injectable } from '@angular/core';

import { RidangoEpic } from '@ridango/core';

import { ActionsObservable } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { RoutesState } from '../../schedule.model';
import { ScheduleService } from '../../schedule.service';
import { RouteDirectionActions } from '../route-direction/route-direction.action';
import { RouteSearchActions } from '../route-search/route-search.action';
import { RoutesActions } from '../routes/routes.action';

@Injectable()
export class RoutesEpics implements RidangoEpic {
    constructor(
        private actions: RoutesActions,
        private service: ScheduleService,
        private routeSearchActions: RouteSearchActions,
        private routeDirectionActions: RouteDirectionActions
    ) {}

    register() {
        return [this.fetchAllStopsWithRoutes, this.fetchAllRoutes];
    }

    fetchAllStopsWithRoutes = (action$: ActionsObservable<any>) =>
        action$.ofType(RoutesActions.FETCH_ALL_STOPS_WITH_ROUTES).pipe(
            switchMap(() => {
                return this.service.fetchStopsWithRoutes().pipe(
                    switchMap((response) => this.normalizeStopsWithRoutes(response)),
                    catchError(() => of(this.actions.fetchAllStopsWithRoutesFail()))
                );
            })
        );

    fetchAllRoutes = (action$: ActionsObservable<any>) =>
        action$.ofType(RoutesActions.FETCH_ALL_ROUTES).pipe(
            switchMap(() => {
                return this.service.fetchRouteList().pipe(
                    map((response) => this.actions.fetchAllRoutesSuccess(this.normalizeRoutes(response))),
                    catchError(() => of(this.actions.fetchAllRoutesFail()))
                );
            })
        );

    private normalizeStopsWithRoutes(response) {
        const directionsByRoute = {};
        const wordsToRoutes = {};

        response.forEach((element) => {
            if (element.stop_routes === undefined) {
                return;
            }

            element.stop_routes.forEach((route) => {
                if (route.route_type === 'N') {
                    return;
                }

                if (directionsByRoute[route.route_id] === undefined) {
                    directionsByRoute[route.route_id] = {};
                }

                let directionId = route.direction_id;
                if (!directionId) {
                    // route direction id is either null or undefined
                    directionId = 0;
                }

                directionsByRoute[route.route_id][directionId] = {
                    route_short_name: route.route_short_name,
                    trip_short_name: route.trip_short_name
                };

                // create stop names to routes lookup
                if (wordsToRoutes[element.stop_name] === undefined) {
                    wordsToRoutes[element.stop_name] = {
                        key: element.stop_name,
                        routeIds: [route.route_id]
                    };
                } else {
                    wordsToRoutes[element.stop_name].routeIds.push(route.route_id);
                }

                // create route name to route lookup
                if (wordsToRoutes[route.route_short_name] === undefined) {
                    wordsToRoutes[route.route_short_name] = {
                        key: route.route_short_name,
                        routeIds: [route.route_id]
                    };
                }
            });
        });

        return [
            this.actions.fetchAllStopsWithRoutesSuccess(),
            this.routeDirectionActions.setRouteDirectionEntity(directionsByRoute),
            this.routeSearchActions.setRouteSearchData(wordsToRoutes)
        ];
    }

    private normalizeRoutes(response): RoutesState {
        const routes: RoutesState = {
            dates: [],
            byDate: {},
            ids: [],
            entities: {},
            selectedId: null
        };

        response.forEach((p) => {
            const byType = {};
            const sortedRoutes = p.routes.sort((x, y) => {
                const a = x.route_short_name;
                const b = y.route_short_name;

                if (a === b) {
                    return 0;
                }

                if (this.isLessThan(a, b)) {
                    return -1;
                } else {
                    return 1;
                }
            });

            routes.dates.push(p.valid_from);
            routes.byDate[p.valid_from] = sortedRoutes.reduce((acc, val) => {
                routes.ids.push(val.route_id);
                if (byType[val.route_type] === undefined) {
                    byType[val.route_type] = [];
                }
                byType[val.route_type].push(val.route_id);
                routes.entities[val.route_id] = val;

                return { ...acc, ...byType };
            }, []);
        });

        return routes;
    }

    private isLessThan(a, b): boolean {
        const notNumberA = isNaN(a);
        const notNumberB = isNaN(b);

        if (!notNumberA && !notNumberB) {
            // number type
            return Number(a) < Number(b);
        } else {
            // compare number part
            const numberA = this.getNumericPrefix(a);
            const numberB = this.getNumericPrefix(b);

            if (numberA === numberB) {
                // number part is the same, do string comparison
                return a < b;
            }

            return numberA < numberB;
        }
    }

    private getNumericPrefix(aString): number {
        let numberPart = 0;

        // if first character is not a number then string has no numeric prefix
        if (!isNaN(aString[0])) {
            const firstCharacterIndex = aString.split('').findIndex((c: any) => isNaN(c));
            numberPart = firstCharacterIndex > 0 ? aString.substring(0, firstCharacterIndex) : aString;
        }

        return Number(numberPart);
    }
}
